export type OfferRow = {
  id: number;
  name: string;
  offer_type: "product" | "category" | "bogo";
  discount_type: "pct" | "flat";
  discount_value: string | number;
  product_id: number | null;
  category_id: number | null;
  buy_qty: number;
  get_qty: number;
};

export type ApplicableOffer = {
  offer: OfferRow;
  reason: string;
  match_qty: number;
};

type LineForOffer = {
  product: { id: number; category_id: number | null };
  qty: number;
  rate: number;
};

export type LineOfferResolution = {
  /** Qty customer pays for (cart input). */
  paidQty: number;
  /** Qty on bill / stock out (paid + free). */
  billQty: number;
  freeQty: number;
  /** Offer discount % on billQty line (0–100). */
  offerDiscPct: number;
  offerLabel: string | null;
  appliedOfferName: string | null;
  offerId: number | null;
  offerDetails: string | null;
};

function flatToDiscPct(flat: number, rate: number, qty: number): number {
  const base = rate * qty;
  if (base <= 0) {
    return 0;
  }

  return Math.min(100, (flat / base) * 100);
}

function bogoFreeQty(paidQty: number, o: OfferRow): number {
  const buy = Math.max(1, Number(o.buy_qty) || 1);
  const get = Math.max(0, Number(o.get_qty) || 0);
  if (get <= 0) {
    return 0;
  }

  return Math.floor(paidQty / buy) * get;
}

/** % off each free unit; default 100% when value unset/zero. */
function bogoFreeUnitDiscPct(o: OfferRow): number {
  const dv = Number(o.discount_value);
  if (o.discount_type === "pct" && Number.isFinite(dv) && dv > 0) {
    return Math.min(100, dv);
  }

  return 100;
}

function bogoLineDiscPct(billQty: number, freeQty: number, freeUnitPct: number): number {
  if (billQty <= 0 || freeQty <= 0) {
    return 0;
  }

  return Math.min(100, (freeQty / billQty) * freeUnitPct);
}

export function formatOfferDetails(o: OfferRow): string {
  if (o.offer_type === "bogo") {
    const buy = Math.max(1, Number(o.buy_qty) || 1);
    const get = Math.max(0, Number(o.get_qty) || 0);
    if (get > 0) {
      return `Buy ${buy} Get ${get} free`;
    }
  }

  const dv = Number(o.discount_value);
  if (!Number.isFinite(dv) || dv <= 0) {
    return o.name;
  }

  if (o.discount_type === "pct") {
    return `${dv}% off`;
  }

  return `₹${dv} off`;
}

export function resolveLineOffer(line: LineForOffer, applicable: ApplicableOffer[]): LineOfferResolution {
  const paidQty = line.qty;
  let freeQty = 0;
  let productDisc = 0;
  let bogoDisc = 0;
  let offerLabel: string | null = null;
  let winningOffer: OfferRow | null = null;

  for (const a of applicable) {
    const o = a.offer;
    if (o.offer_type !== "bogo" || Number(o.product_id) !== line.product.id) {
      continue;
    }
    const fq = bogoFreeQty(paidQty, o);
    if (fq > freeQty) {
      freeQty = fq;
      winningOffer = o;
      offerLabel = `${paidQty} paid + ${fq} free`;
    }
  }

  const billQty = paidQty + freeQty;

  for (const a of applicable) {
    const o = a.offer;
    if (o.offer_type !== "bogo" || Number(o.product_id) !== line.product.id) {
      continue;
    }
    const fq = bogoFreeQty(paidQty, o);
    if (fq <= 0) {
      continue;
    }
    const disc = bogoLineDiscPct(paidQty + fq, fq, bogoFreeUnitDiscPct(o));
    if (disc > bogoDisc) {
      bogoDisc = disc;
      if (freeQty === fq) {
        winningOffer = o;
      }
    }
  }

  for (const a of applicable) {
    const o = a.offer;
    const dv = Number(o.discount_value);
    if (!Number.isFinite(dv) || dv <= 0) {
      continue;
    }

    if (o.offer_type === "product" && Number(o.product_id) === line.product.id) {
      const pct =
        o.discount_type === "pct" ? dv : flatToDiscPct(dv, line.rate, billQty > 0 ? billQty : paidQty);
      if (pct > productDisc) {
        productDisc = pct;
        winningOffer = o;
        offerLabel = o.discount_type === "pct" ? `−${pct}%` : `−₹${dv}`;
      }
    }

    if (
      o.offer_type === "category" &&
      o.category_id != null &&
      line.product.category_id != null &&
      Number(o.category_id) === line.product.category_id
    ) {
      const pct =
        o.discount_type === "pct" ? dv : flatToDiscPct(dv, line.rate, billQty > 0 ? billQty : paidQty);
      if (pct > productDisc) {
        productDisc = pct;
        winningOffer = o;
        offerLabel = o.discount_type === "pct" ? `−${pct}%` : `−₹${dv}`;
      }
    }
  }

  const offerDiscPct = Math.min(100, Math.max(productDisc, bogoDisc));

  if (winningOffer === null && offerDiscPct > 0 && freeQty > 0) {
    for (const a of applicable) {
      const o = a.offer;
      if (o.offer_type === "bogo" && Number(o.product_id) === line.product.id && bogoFreeQty(paidQty, o) === freeQty) {
        winningOffer = o;
        break;
      }
    }
  }

  return {
    paidQty,
    billQty,
    freeQty,
    offerDiscPct,
    offerLabel,
    appliedOfferName: winningOffer?.name ?? null,
    offerId: winningOffer?.id ?? null,
    offerDetails: winningOffer ? formatOfferDetails(winningOffer) : null,
  };
}

export function effectiveLineDiscPct(manualDiscPct: number, offerPct: number): number {
  return Math.min(100, Math.max(manualDiscPct, offerPct));
}

/** @deprecated Use resolveLineOffer */
export function offerDiscPctForLine(line: LineForOffer, applicable: ApplicableOffer[]): number {
  return resolveLineOffer(line, applicable).offerDiscPct;
}

export function cartBillQtyTotal(
  cart: { product: { id: number }; qty: number }[],
  lineOffers: Map<number, LineOfferResolution>,
): number {
  let n = 0;
  for (const c of cart) {
    n += lineOffers.get(c.product.id)?.billQty ?? c.qty;
  }

  return n;
}
